import type { LookGroupDTOType } from '../../../../schemas/styling';
import { stylistLooksApi, type LookGroupsQueryArg } from './api';

type DispatchLike = (action: unknown) => unknown;

type ReorderPinnedGroupsResult = {
  nextGroups: Array<LookGroupDTOType>;
  beforeId: number | undefined;
  afterId: number | undefined;
};

const moveArrayItem = <T>(items: Array<T>, oldIndex: number, newIndex: number): Array<T> => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(oldIndex, 1);

  if (typeof movedItem === 'undefined') {
    return items;
  }

  nextItems.splice(newIndex, 0, movedItem);
  return nextItems;
};

export const sortLookGroups = (groups: Array<LookGroupDTOType>): Array<LookGroupDTOType> =>
  [...groups].sort((first, second) => {
    if (first.is_pinned !== second.is_pinned) {
      return first.is_pinned ? -1 : 1;
    }

    if (first.display_order !== second.display_order) {
      return first.display_order - second.display_order;
    }

    return second.id - first.id;
  });

export const buildLookGroupsQueryArg = (
  occasionId?: number | null,
  subOccasionId?: number | null,
): LookGroupsQueryArg => ({
  occasion: typeof subOccasionId === 'number' ? undefined : (occasionId ?? undefined),
  sub_occasion: subOccasionId ?? undefined,
});

export const patchLookGroupsCache = (
  dispatch: DispatchLike,
  queryArg: LookGroupsQueryArg,
  groups: Array<LookGroupDTOType>,
) =>
  dispatch(
    stylistLooksApi.util.updateQueryData('getLookGroupsList', queryArg, draft => {
      draft.splice(0, draft.length, ...groups);
    }),
  );

export const getPinLookGroupOptimisticState = (
  groups: Array<LookGroupDTOType>,
  groupId: number,
) => {
  const targetGroup = groups.find(group => group.id === groupId);

  if (!targetGroup || targetGroup.is_pinned) {
    return null;
  }

  const primaryLookId = targetGroup.primary_look_id ?? targetGroup.primary_look?.id;
  const nextGroups = sortLookGroups(
    groups.map(group =>
      group.id === groupId
        ? {
            ...group,
            is_pinned: true,
            primary_look_id: primaryLookId,
          }
        : group,
    ),
  );

  return { nextGroups, primaryLookId };
};

export const getUnpinLookGroupOptimisticState = (
  groups: Array<LookGroupDTOType>,
  groupId: number,
) => {
  const targetGroup = groups.find(group => group.id === groupId);

  if (!targetGroup || !targetGroup.is_pinned) {
    return null;
  }

  const nextGroups = sortLookGroups(
    groups.map(group =>
      group.id === groupId
        ? {
            ...group,
            is_pinned: false,
          }
        : group,
    ),
  );

  return { nextGroups };
};

export const getReorderPinnedLookGroupsOptimisticState = (
  groups: Array<LookGroupDTOType>,
  activeId: number,
  overId: number,
): ReorderPinnedGroupsResult | null => {
  const pinnedGroups = groups.filter(group => group.is_pinned);
  const oldIndex = pinnedGroups.findIndex(group => group.id === activeId);
  const newIndex = pinnedGroups.findIndex(group => group.id === overId);

  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return null;
  }

  const reorderedPinnedGroups = moveArrayItem(pinnedGroups, oldIndex, newIndex);
  const beforeId = newIndex > 0 ? reorderedPinnedGroups[newIndex - 1]?.id : undefined;
  const afterId =
    newIndex < reorderedPinnedGroups.length - 1
      ? reorderedPinnedGroups[newIndex + 1]?.id
      : undefined;

  const reorderedPinnedGroupsWithOrder = reorderedPinnedGroups.map((group, index) => ({
    ...group,
    display_order: index + 1,
  }));

  return {
    nextGroups: sortLookGroups([
      ...reorderedPinnedGroupsWithOrder,
      ...groups.filter(group => !group.is_pinned),
    ]),
    beforeId,
    afterId,
  };
};
